import React from "react";
import {
    createRoutesFromChildren,
    matchRoutes,
    useLocation,
    useNavigationType,
    useRoutes,
} from "react-router-dom";
import { wrapUseRoutes } from "@sentry/react";

import * as Sentry from "@sentry/react";
import { isTikTokCallbackDocument } from './utils/tiktokCallback';

// Flag to check if Sentry is initialized
let isSentryInitialized = false;

// Auth-related error patterns that are not actionable bugs — filter
// them out to avoid wasting Sentry quota on expected 401 flows.
const AUTH_ERROR_PATTERNS = [
    'token is invalid or expired',
    'token is blacklisted',
    'given token not valid',
    'authentication credentials were not provided',
    'session has timed out',
    'token_not_valid',
];

const matchesAuthPattern = (msg) => AUTH_ERROR_PATTERNS.some(p => msg.includes(p));

const isAuthError = (event, hint) => {
    const original = hint?.originalException;
    if (original) {
        const msg = (typeof original === 'string' ? original : original?.message || '').toLowerCase();
        if (matchesAuthPattern(msg)) return true;
    }
    const values = event?.exception?.values || [];
    return values.some(v => matchesAuthPattern((v?.value || '').toLowerCase()));
};

// Initialize Sentry for production AND pre-prod. Staging is excluded to avoid
// burning quota on heavy test traffic. Environment tag lets you filter in the
// Sentry dashboard. Previously only production was captured — pre-prod bugs
// were invisible until they hit real users.
const SENTRY_ENVIRONMENTS = {
    'https://dental-sem.co.uk': 'production',
    'https://pre-prod.dental-sem.co.uk': 'pre-production',
};
const sentryEnvironment = SENTRY_ENVIRONMENTS[window.location.origin];
const isTikTokCallback = isTikTokCallbackDocument();

const SENSITIVE_KEY_RE = /^(authorization|auth_code|code|state|access_token|refresh_token|pending_token|hold_token|recovery_token|client_secret|payment_intent_client_secret|slot_token|token|room_url|idempotency-key)$/i;
const SENSITIVE_PAIR_RE = /^(auth_code|code|state|access_token|refresh_token|pending_token|hold_token|recovery_token|client_secret|payment_intent_client_secret|slot_token|token|room_url|idempotency-key)$/i;
const MAX_SANITIZE_DEPTH = 12;
export const isBookingDocument = (url) => {
    try { return /^\/book(?:\/|$)/i.test(decodeURIComponent(new URL(url, window.location.origin).pathname)); }
    catch { return true; }
};
// Once a document visits booking, never upload replay from that document again.
// The transport gate also discards buffered recordings that stop() might flush.
// Non-TikTok OAuth callback documents (TikTok skips Sentry init entirely). Derived from the
// callback routes in src/routes/index.jsx / paths.js; tests/unit/utils verifies every
// `callback` route in that file is matched. Replay starts at init, before the mount effect
// strips ?code&state, so these URLs would otherwise ride along in replay_event.urls.
const OAUTH_CALLBACK_PATH_RES = [
    /^(?:\/dashboard)?\/(?:stripe|facebook|google|google\/ads|google\/analytics)\/authorize\/callback\/?$/i,
    /^\/auth\/(?:google|google\/ads|google-analytics|google\/analytics\/authorize)\/callback\/?$/i,
    /^\/google-(?:ads|analytics)\/callback\/?$/i,
];
export const isOAuthCallbackDocument = (url) => {
    try {
        const path = decodeURIComponent(new URL(url, window.location.origin).pathname);
        return OAUTH_CALLBACK_PATH_RES.some((re) => re.test(path));
    } catch { return true; }
};
export const isReplayBlockedDocument = (url) => isBookingDocument(url) || isOAuthCallbackDocument(url);
let bookingReplayBlocked = isReplayBlockedDocument(window.location.href);
export function bookingReplayTransport(baseTransport) {
    return { ...baseTransport, send(envelope) {
        const items = envelope[1].filter(([header]) => !bookingReplayBlocked || !/^replay_/.test(header.type));
        const header = safeSanitize(envelope[0]);
        const scrubbed = items.map(([itemHeader, payload]) => [itemHeader, itemHeader.type === 'replay_recording' ? payload : safeSanitize(payload)]).filter(([, payload]) => payload !== null);
        return header && scrubbed.length ? baseTransport.send([header, scrubbed]) : Promise.resolve({});
    } };
}
export function installBookingReplayGuard() {
    const block = (url) => {
        if (!isBookingDocument(url)) return;
        bookingReplayBlocked = true;
        try { Promise.resolve(Sentry.getReplay?.()?.stop()).catch(() => {}); } catch { /* Navigation must remain usable. */ }
    };
    ['pushState', 'replaceState'].forEach((method) => {
        const original = window.history[method];
        window.history[method] = function guardedHistory(state, title, url) {
            if (url != null) block(url);
            return original.apply(this, [state, title, url]);
        };
    });
    window.addEventListener('popstate', () => block(window.location.href), true);
}


const PAIR_KEYS = 'auth_code|code|state|access_token|refresh_token|pending_token|hold_token|recovery_token|client_secret|payment_intent_client_secret|slot_token|token|room_url|idempotency-key';
// key=value pairs after a start-of-string, ?, &, #, ;, comma or whitespace boundary.
const QUERY_PAIR_RE = new RegExp(String.raw`(^|[?&#;,\s])((?:${PAIR_KEYS})=)[^&#;\s"']*`, 'gi');
// "key": "value" / 'key': value, including backslash-escaped quotes from JSON nested in a string.
const JSON_PAIR_RE = new RegExp(String.raw`(\\*["'])(${PAIR_KEYS})(\\*["']\s*:\s*)(\\*"(?:[^"\\]|\\(?!"))*\\*"|\\*'[^']*\\*'|[^,}\]\s]+)`, 'gi');
const redactSensitivePairs = (str) => str
    .replace(QUERY_PAIR_RE, '$1$2[Filtered]')
    .replace(JSON_PAIR_RE, '$1$2$3"[Filtered]"');
const scrubBookingPaths = (str) => str
    .replace(/(\/book\/virtual\/)[^/?#\s]+/gi, '$1[Filtered]')
    .replace(/(\/api\/v1\/public\/virtual-consultations\/)[^/?#\s]+/gi, '$1[Filtered]')
    .replace(/(\/api\/v1\/public\/book\/)[^/?#\s]+(?=\/(?:reschedule|cancel)(?:[/?#\s]|$))/gi, '$1[Filtered]')
    .replace(/(\/book\/reschedule\/)[^/?#\s]+/gi, '$1[Filtered]')
    .replace(/(\/book\/)[^/?#\s]+(?=\/cancel(?:[/?#\s]|$))/gi, '$1[Filtered]');

const decodePercent = (str) => str.replace(/(?:%[0-9a-f]{2})+/gi, (part) => {
    try { return decodeURIComponent(part); } catch { return '[Filtered]'; }
});

const isPlainObject = (value) => {
    if (Object.prototype.toString.call(value) !== '[object Object]') return false;
    const proto = Object.getPrototypeOf(value);
    return proto === Object.prototype || proto === null;
};

// Cycle-safe (back-references become '[Circular]'), depth-capped (deeper subtrees become
// '[Truncated]') and limited to plain arrays/objects, so it can never overflow the stack.
// Non-plain objects pass through: the SDK hands beforeSend a JSON-normalized event
// (normalizeDepth), so class instances / DOM nodes never reach it, and the previous
// behaviour of flattening them to {} destroyed data rather than protecting it.
const sanitizeValue = (value, depth, inProgress, done) => {
    if (typeof value === 'string') {
        const decoded = decodePercent(value);
        // Router paths are decoded before matching. Encoded booking URLs can
        // contain encoded separators too, so omit the whole string safely.
        if (decoded !== value && /\/book(?:\/|$)/i.test(decoded)) return '[Filtered booking URL]';
        const redacted = redactSensitivePairs(scrubBookingPaths(value));
        // Percent-encoded keys/separators (?c%6fde=..., ?code%3D...) only become visible once decoded.
        // Prefer the original (still-encoded) string whenever it needed no further scrubbing.
        if (decoded !== value) {
            const decodedRedacted = decodePercent(redacted);
            const stillLeaking = redactSensitivePairs(decodedRedacted);
            if (stillLeaking !== decodedRedacted) return stillLeaking;
        }
        return redacted;
    }
    if (!value || typeof value !== 'object') return value;
    const isArray = Array.isArray(value);
    if (!isArray && !isPlainObject(value)) return value;
    if (inProgress.has(value)) return '[Circular]';
    if (depth >= MAX_SANITIZE_DEPTH) return '[Truncated]';
    if (done.has(value)) return done.get(value);
    if (isArray && value.length === 2 && typeof value[0] === 'string' && SENSITIVE_PAIR_RE.test(value[0])) {
        return [value[0], '[Filtered]'];
    }
    inProgress.add(value);
    let result;
    const stripeCallbackBody = typeof value.url === 'string' && /\/api\/v1\/stripe\/(?:connect\/)?callback\/?(?:[?#]|$)/i.test(value.url);
    try {
        result = isArray
            ? value.map((item) => sanitizeValue(item, depth + 1, inProgress, done))
            : Object.fromEntries(Object.entries(value).map(([key, item]) => [key,
                SENSITIVE_KEY_RE.test(key) || (stripeCallbackBody && /^(data|body|request_body)$/i.test(key)) ? '[Filtered]' : sanitizeValue(item, depth + 1, inProgress, done),
            ]));
    } finally {
        inProgress.delete(value);
    }
    done.set(value, result);
    return result;
};

// Defense in depth for navigation breadcrumbs, request URLs, query strings and referrers.
// Replay is deliberately not initialized on any supported callback document (its initial
// snapshot precedes React effects and contains location.href).
export function sanitizeOAuthTelemetry(value) {
    return sanitizeValue(value, 0, new WeakSet(), new WeakMap());
}

// Fail closed when a telemetry payload cannot be scrubbed.
export function safeSanitize(event) {
    try {
        return sanitizeOAuthTelemetry(event);
    } catch (error) {
        return null;
    }
}

if (sentryEnvironment && !isTikTokCallback) {
    installBookingReplayGuard();
    Sentry.init({
        transport: (options) => bookingReplayTransport(Sentry.makeFetchTransport(options)),
        beforeBreadcrumb: safeSanitize,
        dsn: "https://6e2eb0942fb174e880057e2406184025@o1361470.ingest.us.sentry.io/4508273023582208",
        environment: sentryEnvironment,
        sendDefaultPii: false,
        ignoreErrors: [
            // Browser extensions (Grammarly, etc.) and React DOM race conditions
            "Failed to execute 'removeChild' on 'Node'",
            "Failed to execute 'insertBefore' on 'Node'",
            // Stale chunk errors after deploys — handled by lazyRetry reload
            'Loading chunk',
            'Loading CSS chunk',
            'Importing a module script failed',
            // ResizeObserver noise from MUI/browser
            'ResizeObserver loop',
        ],
        integrations: [
            Sentry.reactRouterV6BrowserTracingIntegration({
                useEffect: React.useEffect,
                useLocation,
                useNavigationType,
                createRoutesFromChildren,
                matchRoutes,
            }),
            ...(!bookingReplayBlocked ? [Sentry.replayIntegration({
                beforeAddRecordingEvent: safeSanitize,
                beforeErrorSampling: () => !bookingReplayBlocked,
                // Mask all text in replays to protect patient PII (GDPR)
                maskAllText: true,
                maskTextSelector: null,
                blockAllMedia: false,

                // Capture network metadata only — no request/response bodies (GDPR)
                networkDetailAllowUrls: [/^https?:\/\//],
                networkCaptureBodies: false,
                // Capture a broad set of headers; some may still be unavailable due to browser/CORS
                networkRequestHeaders: [
                    'Content-Type',
                    'Accept',
                    'Accept-Language',
                    'Cache-Control',
                    'Pragma',
                    'Origin',
                ],
                networkResponseHeaders: [
                    'Content-Type',
                    'Content-Length',
                    'Cache-Control',
                    'Date',
                    'Server',
                ],
            })] : []),
        ],
        // Filter out noisy auth errors before they consume Sentry quota
        beforeSend(event, hint) {
            if (isAuthError(event, hint)) return null;
            return safeSanitize(event);
        },
        beforeSendTransaction: safeSanitize,
        // Sample 20% of transactions — 100% was excessive and costly
        tracesSampleRate: 0.2,
        // Set `tracePropagationTargets` to control for which URLs trace propagation should be enabled
        tracePropagationTargets: [/^\//, /^https:\/\/api\.dental-sem\.co\.uk/],

        // Capture Replay for 10% of all sessions,
        // plus 100% of sessions with an error (restored from 50% — error volume
        // is now very low after the Sentry fix sprint, so cost is negligible).
        replaysSessionSampleRate: 0.1,
        replaysOnErrorSampleRate: 1.0,
    });
    isSentryInitialized = true;
}

// Use Sentry routes wrapper only if Sentry is initialized
export const useSentryRoutes = isSentryInitialized
    ? wrapUseRoutes(useRoutes)
    : useRoutes;
